<template>
    <settings-wrapper title="Space settings" section="General">
        <v-row>
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title>Space details</v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <label>Name</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ currentSpaceName }}</v-sheet>
                            </v-col>
                            <v-col>
                                <label>Description</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ getSpaceDescription }}</v-sheet>
                            </v-col>
                        </v-row>
                        <v-row v-if="currentSpaceType === spaceTypes.EDUCATION_SPACE">
                            <v-col cols="6">
                                <label>Archive on</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">
                                    <span class="subtitle-2 secondary--text">{{ spaceArchivalDate }}</span>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-actions>
                        <space-details-edit-dialog
                            v-if="isInstanceEditor"
                            :space-name="currentSpaceName"
                            :space-description="getSpaceDescription"
                            :archive-by-date="spaceArchivalDate"
                            :max-archive-by-date="furthestArchivalDate"
                            :show-archive-by-date="currentSpaceType === spaceTypes.EDUCATION_SPACE"
                            :can-edit-archive-by-date="isInstanceEditor && isSpaceAdmin && !isSpaceArchived"
                            @updated="refreshSpaceDetails" />
                    </v-card-actions>
                </v-card>
            </v-col>
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title>Space status</v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12" md="6">
                                <label>Visibility</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">
                                    <span class="subtitle-2 secondary--text" v-if="currentSpaceVisibility === spaceVisibilityCodes.PUBLIC_SPACE">Public</span>
                                    <span class="subtitle-2 secondary--text" v-if="currentSpaceVisibility === spaceVisibilityCodes.PRIVATE_SPACE">Private</span>
                                    <span class="subtitle-2 secondary--text" v-if="currentSpaceVisibility === spaceVisibilityCodes.FACULTY_ONLY_SPACE">
                                        Faculty only
                                    </span>
                                </v-sheet>
                            </v-col>
                            <v-col cols="12" md="6">
                                <label>Lifecycle phase</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">
                                    <span class="subtitle-2 secondary--text" v-if="!isSpaceArchived">Live</span>
                                    <span class="subtitle-2 secondary--text" v-if="isSpaceArchived">
                                        Archived on {{ spaceArchiveTimestamp | dateTimeToHuman }}
                                    </span>
                                </v-sheet>
                            </v-col>
                            <v-col cols="6" v-if="isSpaceAdmin">
                                <label>Role</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">
                                    <v-icon small color="secondary" class="mr-1">security</v-icon>
                                    <span class="subtitle-2 secondary--text">Administrator</span>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row v-if="currentSpaceType === spaceTypes.RESEARCH_SPACE && isSpaceAdmin" class="mt-6">
            <v-col cols="12">
                <h3 class="secondary--text">Space Lifecycle</h3>
            </v-col>
        </v-row>
        <v-row v-if="currentSpaceType === spaceTypes.RESEARCH_SPACE && isSpaceAdmin" class="mb-6">
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title>Automatic resting</v-card-title>
                    <v-form :key="currentSpace && currentSpace.sid" ref="restingForm" v-model="restingFormValid" @submit.prevent="saveRestingSettings">
                        <v-card-text class="pt-0 pb-0">
                            <v-row dense>
                                <v-col cols="12" class="mb-3">
                                    <label>Organization policy</label>
                                    <v-sheet color="grey lighten-4" rounded class="pa-3 d-flex align-center">
                                        <span class="subtitle-2 secondary--text">{{ organizationRestingPolicy }}</span>
                                    </v-sheet>
                                </v-col>
                                <v-col cols="12">
                                    <v-select
                                        v-model="restingPolicy"
                                        :items="restingPolicies"
                                        label="Resting policy"
                                        outlined
                                        dense
                                        :disabled="!canEditRestingSettings"></v-select>
                                </v-col>
                                <v-col v-if="restingPolicy === 'custom'" cols="12">
                                    <v-text-field
                                        v-model="restingThresholdDays"
                                        v-mask="integerMask"
                                        label="Days since last application start"
                                        inputmode="numeric"
                                        outlined
                                        dense
                                        :rules="[positiveWholeDays]"
                                        :disabled="!canEditRestingSettings"
                                        required></v-text-field>
                                </v-col>
                            </v-row>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                                type="submit"
                                text
                                color="primary"
                                :loading="savingRestingSettings"
                                :disabled="!canEditRestingSettings || !restingSettingsChanged || (restingPolicy === 'custom' && !restingFormValid)">
                                Save
                            </v-btn>
                        </v-card-actions>
                    </v-form>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <h3 class="error--text">Danger Zone</h3>
            </v-col>
        </v-row>

        <v-row>
            <v-col v-if="isSpaceAdmin" cols="12" md="6">
                <v-card>
                    <v-card-title>Delete space</v-card-title>
                    <v-card-actions>
                        <EntityDeleteDialog entity-type="space" :organization-id="activeOrgId" :space-id="activeSpaceId" :space-name="currentSpaceName" />
                    </v-card-actions>
                </v-card>
            </v-col>
            <v-col cols="12" :md="isSpaceAdmin ? 6 : 12">
                <v-card>
                    <v-card-title>Leave space</v-card-title>
                    <v-card-actions>
                        <EntityLeaveDialog entity-type="space" />
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </settings-wrapper>
</template>

<script>
import { mapState } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { DateTime } from 'luxon'
import { isAdminRole, isEditorRole } from '@/utils'
import SettingsWrapper from '@/components/SettingsWrapper.vue'
import EntityLeaveDialog from '@/components/EntityLeaveDialog.vue'
import EntityDeleteDialog from '@/components/EntityDeleteDialog.vue'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

const integerMask = createNumberMask({
    prefix: '',
    includeThousandsSeparator: false,
    allowDecimal: false,
    allowNegative: false
})

const SpaceDetailsEditDialog = () => import('../components/SpaceDetailsEditDialog.vue')

export default {
    name: 'SpaceConfiguration',
    mixins: [enumsData],
    components: {
        SettingsWrapper,
        SpaceDetailsEditDialog,
        EntityLeaveDialog,
        EntityDeleteDialog
    },
    data() {
        return {
            integerMask,
            restingPolicy: 'inherit',
            restingThresholdDays: null,
            restingFormValid: false,
            savingRestingSettings: false,
            loadingSpaceContext: false,
            restingPolicies: [
                { text: 'Use organization policy', value: 'inherit' },
                { text: 'Custom threshold', value: 'custom' },
                { text: 'Disabled', value: 'disabled' }
            ]
        }
    },
    async created() {
        if (this.activeOrgId && !this.spacesWithPrimarySnapshots.length) {
            await this.$store.dispatch('orgStore/fetchOrgSpaces', this.activeOrgId)
        }
    },
    methods: {
        positiveWholeDays(value) {
            return (Number.isSafeInteger(Number(value)) && Number(value) > 0) || 'Enter a positive whole number of days'
        },
        async saveRestingSettings() {
            if (!this.canEditRestingSettings || !this.restingSettingsChanged) return
            if (this.restingPolicy === 'custom' && !this.$refs.restingForm.validate()) return
            const sid = this.activeSpaceId
            const oid = this.activeOrgId
            const configs = { ...(this.currentSpace.space_configs || {}) }
            if (this.restingPolicy === 'inherit') {
                delete configs.resting_policy
            } else {
                configs.resting_policy = this.restingPolicy
            }
            if (this.restingPolicy === 'custom') {
                configs.resting_threshold_days = Number(this.restingThresholdDays)
            } else {
                delete configs.resting_threshold_days
            }
            let settingsSaved = false
            this.savingRestingSettings = true
            try {
                const { data } = await this.$axios.patch(`/spaces/${sid}`, { space_configs: configs })
                settingsSaved = true
                if (String(this.activeSpaceId) === String(sid)) {
                    this.$store.commit('spaceStore/setCurrentSpace', data)
                }
                const { data: refreshedSpace } = await this.$axios.get(`/spaces/${sid}`)
                if (String(this.activeSpaceId) === String(sid)) {
                    this.$store.commit('spaceStore/setCurrentSpace', refreshedSpace)
                }
                if (String(this.activeOrgId) === String(oid)) {
                    await this.$store.dispatch('orgStore/fetchOrgSpaces', oid)
                }
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Automatic resting settings saved.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: settingsSaved ? 'Settings saved, but failed to refresh space data.' : 'Failed to save automatic resting settings.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            } finally {
                this.savingRestingSettings = false
            }
        },
        refreshSpaceDetails(data) {
            this.$store.commit('spaceStore/setCurrentSpace', data)
            this.$store.dispatch('orgStore/fetchOrgSpaces', this.activeOrgId)
        },
        async loadActiveSpaceContext() {
            if (!this.activeSpaceId) {
                return
            }
            const sid = this.activeSpaceId
            this.loadingSpaceContext = true
            try {
                const { data } = await this.$axios.get(`/spaces/${sid}`)
                if (String(this.activeSpaceId) === String(sid)) {
                    this.$store.commit('spaceStore/setCurrentSpace', data)
                }
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to load selected space configuration.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            } finally {
                if (String(this.activeSpaceId) === String(sid)) {
                    this.loadingSpaceContext = false
                }
            }
        }
    },
    computed: {
        ...mapState(['currentOrgData']),
        ...mapState('orgStore', ['spacesWithPrimarySnapshots']),
        ...mapState('spaceStore', ['currentSpace']),
        canEditRestingSettings() {
            return (
                this.isSpaceAdmin &&
                this.currentSpaceType === this.spaceTypes.RESEARCH_SPACE &&
                String(this.currentSpace?.sid) === String(this.activeSpaceId) &&
                !this.loadingSpaceContext &&
                !this.savingRestingSettings &&
                !this.isSpaceArchived &&
                !this.currentSpace?.rested_timestamp &&
                this.currentSpace?.lock_mode == null
            )
        },
        restingSettingsChanged() {
            const configs = this.currentSpace?.space_configs || {}
            return (
                this.restingPolicy !== (configs.resting_policy || 'inherit') ||
                (this.restingPolicy === 'custom' && Number(this.restingThresholdDays) !== Number(configs.resting_threshold_days))
            )
        },
        organizationRestingPolicy() {
            if (String(this.currentOrgData?.oid) !== String(this.activeOrgId)) return 'Unavailable'
            const configs = this.currentOrgData?.org_configs || {}
            if (
                (configs.resting_space_types?.length && !configs.resting_space_types.includes(this.spaceTypes.RESEARCH_SPACE)) ||
                (configs.resting_only_sids?.length && !configs.resting_only_sids.some(sid => String(sid) === String(this.activeSpaceId)))
            ) {
                return 'Disabled for this project'
            }
            if (!configs.resting_trigger || !configs.resting_threshold_days) return 'Disabled'
            if (configs.resting_trigger !== 'app_start_activity') return 'Unsupported policy'
            return `${configs.resting_threshold_days} days since last application start`
        },
        activeOrgId() {
            return this.$route.params.oid
        },
        activeSpaceId() {
            return this.$route.params.sid
        },
        activeSpaceData() {
            if (!this.activeSpaceId) {
                return null
            }
            return this.spacesWithPrimarySnapshots.find(space => space.sid.toString() === this.activeSpaceId.toString()) || null
        },
        isSpaceAdmin() {
            return isAdminRole(this.activeSpaceData?.space_role)
        },
        isInstanceEditor() {
            const role = this.activeSpaceData?.space_role
            return isAdminRole(role) || isEditorRole(role)
        },
        currentSpaceType() {
            return this.activeSpaceData?.space_type || null
        },
        currentSpaceVisibility() {
            return this.activeSpaceData?.space_visibility_type || null
        },
        isSpaceArchived() {
            return Boolean(
                this.currentSpace?.archival_timestamp || this.currentSpace?.space_archival_timestamp || this.activeSpaceData?.space_archival_timestamp
            )
        },
        currentSpaceName() {
            return this.currentSpace?.long_id || this.activeSpaceData?.space_long_id || ''
        },
        spaceArchivalDate() {
            return this.activeSpaceData?.space_archive_by_date || null
        },
        spaceCreationDate() {
            return this.activeSpaceData?.space_creation_timestamp || null
        },
        getSpaceDescription() {
            return this.activeSpaceData?.space_description || 'No description available.'
        },
        spaceArchiveTimestamp() {
            return this.activeSpaceData?.space_archival_timestamp || null
        },
        furthestArchivalDate() {
            if (!this.spaceCreationDate) {
                return null
            }
            return DateTime.fromISO(this.spaceCreationDate).plus({ years: 1 }).toISODate()
        }
    },
    watch: {
        currentSpace: {
            immediate: true,
            handler(space) {
                if (String(space?.sid) !== String(this.activeSpaceId)) return
                this.restingPolicy = space?.space_configs?.resting_policy || 'inherit'
                this.restingThresholdDays = space?.space_configs?.resting_threshold_days ?? null
            }
        },
        spacesWithPrimarySnapshots: {
            immediate: true,
            handler(spaces) {
                if (!this.$route.params.sid && Array.isArray(spaces) && spaces.length) {
                    this.$router.replace({
                        name: this.$route.name,
                        params: {
                            ...this.$route.params,
                            sid: spaces[0].sid
                        },
                        query: { ...this.$route.query }
                    })
                }
            }
        },
        activeSpaceId: {
            immediate: true,
            handler() {
                if (this.activeSpaceId) {
                    this.loadActiveSpaceContext()
                }
            }
        }
    }
}
</script>
